/**
 * DemoQuestionnaire · Modal de onboarding bloqueante para usuarios demo-start.
 *
 * Sprint feat/demo-start-access · 2026-07-01 · Carlos Beltrán (IA)
 *
 * Behavior:
 * - Visible si profile.demo_plan === 'demo-start' && !profile.demo_onboarding_completed_at
 * - Modal bloqueante: NO puede cerrarse sin enviar el formulario.
 * - Paso 1: cuestionario (nombre, apellido, whatsapp, nivel, capital, broker, llamada)
 * - Paso 2 (tras submit): prompt Telegram (usa componente existente) — PUEDE cerrarse.
 * - Al completar paso 2 o cerrar: recarga profile via window.dispatchEvent('profile:refresh')
 *   o navega a /dashboard.
 *
 * whatsapp: si ya existe en profile (pre-relleno desde GHL) → input bloqueado.
 *           si no → input editable.
 */
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { authBearer } from '../lib/impersonation';
import type { AuthIdentity } from '../lib/types';

const API_URL = import.meta.env.VITE_API_URL ?? '';

interface DemoQuestionnaireProps {
  whatsapp: string | null;
  identity: AuthIdentity | null;
  onComplete?: () => void;
}

type NivelDigital = 'novato' | 'intermedio' | 'experto';
type CapitalDisponible = '<1000' | '1000-5000' | '>5000';

export default function DemoQuestionnaire({
  whatsapp: initialWhatsapp,
  identity,
  onComplete,
}: DemoQuestionnaireProps): JSX.Element {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return <></>;

  // Form fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [whatsapp, setWhatsapp] = useState(initialWhatsapp ?? '');
  const [nivelDigital, setNivelDigital] = useState<NivelDigital | ''>('');
  const [capitalDisponible, setCapitalDisponible] = useState<CapitalDisponible | ''>('');
  const [tieneBroker, setTieneBroker] = useState<boolean | null>(null);
  const [quiereLlamada, setQuiereLlamada] = useState<boolean | null>(null);

  const isFormValid =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    nivelDigital !== '' &&
    capitalDisponible !== '' &&
    tieneBroker !== null &&
    quiereLlamada !== null;

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!isFormValid) return;
    setLoading(true);
    setSubmitError(null);
    try {
      const token = authBearer(identity);
      const resp = await fetch(`${API_URL}/api/me/demo/onboarding`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          whatsapp: whatsapp.trim() || null,
          nivel_digital: nivelDigital,
          capital_disponible: capitalDisponible,
          tiene_broker: tieneBroker,
          quiere_llamada: quiereLlamada,
        }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        setSubmitError((data as { detail?: string })?.detail ?? 'Error al enviar · inténtalo de nuevo');
        return;
      }
      onComplete?.();
      window.dispatchEvent(new Event('profile:refresh'));
      navigate('/dashboard', { replace: true });
    } catch {
      setSubmitError('Error de conexión · comprueba tu internet e inténtalo de nuevo');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="demo-questionnaire-title"
      data-testid="demo-questionnaire-modal"
    >
      <AnimatePresence mode="wait">
        <motion.div
            key="questionnaire"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="card w-full max-w-lg overflow-y-auto p-8"
            style={{ maxHeight: '90vh' }}
          >
            <div className="mb-6 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-violet-500/15 text-2xl">
                👤
              </div>
              <h2 id="demo-questionnaire-title" className="text-xl font-semibold text-ink">
                Cuéntanos un poco sobre ti
              </h2>
              <p className="mt-1 text-sm text-ink/60">
                Tardas menos de 1 minuto · nos ayuda a darte el mejor soporte
              </p>
            </div>

            {submitError && (
              <div className="mb-4 rounded-lg border border-red-400/30 bg-red-500/10 p-3 text-sm text-red-400">
                {submitError}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Nombre + Apellido */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="demo-first-name" className="mb-1 block text-sm font-medium text-ink/80">
                    Nombre *
                  </label>
                  <input
                    id="demo-first-name"
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="input w-full"
                    placeholder="Ana"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label htmlFor="demo-last-name" className="mb-1 block text-sm font-medium text-ink/80">
                    Apellido *
                  </label>
                  <input
                    id="demo-last-name"
                    type="text"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="input w-full"
                    placeholder="García"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* WhatsApp */}
              <div>
                <label htmlFor="demo-whatsapp" className="mb-1 block text-sm font-medium text-ink/80">
                  Teléfono WhatsApp
                </label>
                <input
                  id="demo-whatsapp"
                  type="tel"
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value)}
                  className="input w-full"
                  placeholder="+34 600 000 000"
                  disabled={loading || !!initialWhatsapp}
                />
                {initialWhatsapp && (
                  <p className="mt-1 text-xs text-ink/40">Teléfono ya registrado</p>
                )}
              </div>

              {/* Nivel digital */}
              <fieldset>
                <legend className="mb-2 block text-sm font-medium text-ink/80">
                  ¿Cuál es tu nivel digital? *
                </legend>
                <div className="flex flex-col gap-2 sm:flex-row">
                  {([['novato', 'Soy muy novato'], ['intermedio', 'Me manejo bien'], ['experto', 'Soy experto']] as const).map(
                    ([val, label]) => (
                      <label
                        key={val}
                        className={`flex flex-1 cursor-pointer items-center gap-2 rounded-lg border p-3 text-sm transition-colors ${
                          nivelDigital === val
                            ? 'border-violet-500 bg-violet-500/10 text-ink'
                            : 'border-ink/15 bg-ink/5 text-ink/70 hover:border-ink/30'
                        }`}
                      >
                        <input
                          type="radio"
                          name="nivel_digital"
                          value={val}
                          checked={nivelDigital === val}
                          onChange={() => setNivelDigital(val)}
                          className="sr-only"
                          required
                        />
                        {label}
                      </label>
                    )
                  )}
                </div>
              </fieldset>

              {/* Capital disponible */}
              <fieldset>
                <legend className="mb-2 block text-sm font-medium text-ink/80">
                  Capital disponible para operar *
                </legend>
                <div className="flex flex-col gap-2 sm:flex-row">
                  {([['<1000', 'Menos de 1.000€'], ['1000-5000', '1.000 – 5.000€'], ['>5000', 'Más de 5.000€']] as const).map(
                    ([val, label]) => (
                      <label
                        key={val}
                        className={`flex flex-1 cursor-pointer items-center gap-2 rounded-lg border p-3 text-sm transition-colors ${
                          capitalDisponible === val
                            ? 'border-violet-500 bg-violet-500/10 text-ink'
                            : 'border-ink/15 bg-ink/5 text-ink/70 hover:border-ink/30'
                        }`}
                      >
                        <input
                          type="radio"
                          name="capital"
                          value={val}
                          checked={capitalDisponible === val}
                          onChange={() => setCapitalDisponible(val)}
                          className="sr-only"
                          required
                        />
                        {label}
                      </label>
                    )
                  )}
                </div>
              </fieldset>

              {/* ¿Tienes broker? */}
              <fieldset>
                <legend className="mb-2 block text-sm font-medium text-ink/80">
                  ¿Tienes broker actualmente? *
                </legend>
                <div className="flex gap-3">
                  {[['true', 'Sí'], ['false', 'No']].map(([val, label]) => (
                    <label
                      key={val}
                      className={`flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border p-3 text-sm transition-colors ${
                        String(tieneBroker) === val
                          ? 'border-violet-500 bg-violet-500/10 text-ink'
                          : 'border-ink/15 bg-ink/5 text-ink/70 hover:border-ink/30'
                      }`}
                    >
                      <input
                        type="radio"
                        name="tiene_broker"
                        value={val}
                        checked={String(tieneBroker) === val}
                        onChange={() => setTieneBroker(val === 'true')}
                        className="sr-only"
                        required
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </fieldset>

              {/* ¿Quieres que te llamemos? */}
              <fieldset>
                <legend className="mb-2 block text-sm font-medium text-ink/80">
                  ¿Quieres que te llamemos para orientarte?
                </legend>
                <div className="flex gap-3">
                  {[['true', 'Sí, me interesa'], ['false', 'Ahora no']].map(([val, label]) => (
                    <label
                      key={val}
                      className={`flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border p-3 text-sm transition-colors ${
                        String(quiereLlamada) === val
                          ? 'border-violet-500 bg-violet-500/10 text-ink'
                          : 'border-ink/15 bg-ink/5 text-ink/70 hover:border-ink/30'
                      }`}
                    >
                      <input
                        type="radio"
                        name="quiere_llamada"
                        value={val}
                        checked={String(quiereLlamada) === val}
                        onChange={() => setQuiereLlamada(val === 'true')}
                        className="sr-only"
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </fieldset>

              <button
                type="submit"
                disabled={!isFormValid || loading}
                className="btn-primary mt-2 w-full disabled:opacity-50"
                data-testid="demo-questionnaire-submit"
              >
                {loading ? 'Enviando...' : 'Continuar →'}
              </button>

              <button
                type="button"
                onClick={() => setDismissed(true)}
                className="mt-3 block w-full text-center text-xs text-ink/40 hover:text-ink/60"
                data-testid="demo-questionnaire-skip"
              >
                Omitir por ahora y entrar al campus
              </button>
            </form>
          </motion.div>
      </AnimatePresence>
    </div>
  );
}
